import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import redditApi from '../../../services/redditApi';
import RhDatePicker from './RhDatePicker';
import { NicheTags } from '../components/AccountQualityCheck';
import { NicheFitModal } from '../components/NicheFitModal';
import ArchiveToggle from '../components/ArchiveToggle';

/* ─── Inline Select (mirrors OverviewTab pattern) ───────────────────────── */

function Select({ value, onChange, options, placeholder, searchable = true }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const selected = options.find((o) => o.value === value);

  const filtered = searchable && search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  function openMenu() {
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 220);
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuHeight = Math.min(options.length * 40 + 48, 300);
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;
    const wouldOverflowRight = rect.left + menuWidth > window.innerWidth - 12;
    const horizontalStyle = wouldOverflowRight
      ? { right: window.innerWidth - rect.right, left: 'auto' }
      : { left: rect.left };
    setMenuStyle({
      position: 'fixed',
      width: menuWidth,
      zIndex: 9999,
      ...horizontalStyle,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
    setOpen(true);
    setTimeout(() => searchRef.current?.focus(), 20);
  }

  function close() {
    setOpen(false);
    setSearch('');
  }

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) close();
    }
    function handleScroll(e) {
      // Keep open when scrolling inside the menu itself
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      close();
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <div className={`rh-custom-select${open ? ' rh-custom-select--open' : ''}`}>
      <button
        type="button"
        ref={triggerRef}
        className="rh-custom-select-trigger"
        onClick={() => (open ? close() : openMenu())}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <svg className="rh-custom-select-chevron" width="12" height="12" fill="none"
          stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && createPortal(
        <div className="rh-custom-select-menu" ref={menuRef} style={menuStyle}>
          {searchable && (
            <div className="rh-custom-select-search">
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" viewBox="0 0 24 24" style={{ flexShrink: 0, opacity: 0.45 }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="rh-custom-select-search-input"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')}
                  className="rh-custom-select-search-clear">✕</button>
              )}
            </div>
          )}
          <div className="rh-custom-select-options">
            {filtered.length === 0 ? (
              <div className="rh-custom-select-empty">No results</div>
            ) : filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`rh-custom-select-option${value === o.value ? ' selected' : ''}`}
                onClick={() => { onChange(o.value); close(); }}
              >
                {value === o.value && (
                  <svg width="12" height="12" fill="none" stroke="currentColor"
                    strokeWidth="2.5" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {o.label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ─── Compact pill list: shortest item + "+N" (single-line, fixed row height) ─ */

function PillList({ items, colorClass }) {
  if (!items || items.length === 0) return <span className="rh-muted">—</span>;

  const ordered = [...items].sort((a, b) => String(a).length - String(b).length
    || String(a).localeCompare(String(b)));
  const shortest = ordered[0];
  const hidden = ordered.slice(1);

  return (
    <div
      style={{
        display: 'inline-flex', flexWrap: 'nowrap', gap: '4px', alignItems: 'center',
        whiteSpace: 'nowrap', maxWidth: '100%',
      }}
      title={items.join(', ')}
    >
      <span
        className={`rh-badge ${colorClass || 'rh-badge-submitted'}`}
        style={{
          fontSize: '0.7rem', padding: '1px 6px',
          maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {shortest}
      </span>
      {hidden.length > 0 && (
        <span
          className="rh-badge rh-badge-archived"
          style={{ fontSize: '0.7rem', padding: '1px 6px', flexShrink: 0 }}
          title={hidden.join(', ')}
        >
          +{hidden.length}
        </span>
      )}
    </div>
  );
}

/* ─── Sortable column header (horizontal) ───────────────────────────────── */

function SortTh({ col, label, sort, onSort, style, title }) {
  const active = sort.col === col;
  return (
    <th
      title={title}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      onClick={() => onSort(col)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <svg
          width="10" height="10" fill="none" stroke="currentColor"
          strokeWidth="2.5" viewBox="0 0 24 24"
          style={{ opacity: active ? 1 : 0.3, transition: 'opacity 0.15s' }}
        >
          {active && sort.dir === 'asc'
            ? <polyline points="18 15 12 9 6 15" />
            : <polyline points="6 9 12 15 18 9" />
          }
        </svg>
      </span>
    </th>
  );
}

/* ─── Sortable column header (vertical, narrow) ─────────────────────────── */

function VerticalSortTh({ col, label, sort, onSort, title }) {
  const active = sort.col === col;
  return (
    <th
      title={title || label}
      onClick={() => onSort(col)}
      style={{
        cursor: 'pointer', userSelect: 'none',
        width: '42px', minWidth: '42px', maxWidth: '42px',
        padding: '8px 4px', verticalAlign: 'bottom', textAlign: 'center',
      }}
    >
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
      }}>
        <span style={{
          writingMode: 'vertical-lr',
          transform: 'rotate(180deg)',
          fontSize: '0.72rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          lineHeight: 1,
          color: active ? 'var(--rh-orange, #e55)' : 'inherit',
        }}>
          {label}
        </span>
        <svg
          width="9" height="9" fill="none" stroke="currentColor"
          strokeWidth="2.5" viewBox="0 0 24 24"
          style={{ opacity: active ? 1 : 0.25, transition: 'opacity 0.15s', flexShrink: 0 }}
        >
          {active && sort.dir === 'asc'
            ? <polyline points="18 15 12 9 6 15" />
            : <polyline points="6 9 12 15 18 9" />
          }
        </svg>
      </div>
    </th>
  );
}

/* ─── Deletion rate cell ─────────────────────────────────────────────────── */

function RateCell({ rate, compact }) {
  const cls =
    rate >= 50 ? 'rh-badge-deleted' :
    rate >= 25 ? 'rh-badge-overdue' :
    rate > 0   ? 'rh-badge-dws'     :
                 'rh-badge-live';
  return (
    <span className={`rh-badge ${cls}`} style={{ minWidth: compact ? '36px' : '48px', textAlign: 'center', fontSize: compact ? '0.72rem' : undefined }}>
      {rate}%
    </span>
  );
}

/* ─── Account quality status badge ───────────────────────────────────────── */

const STATUS_LABELS = {
  passed: 'Passed',
  failed: 'Failed',
  needs_manual_review: 'Needs Review',
  error: 'Error',
  checking: 'Checking',
  banned: 'Banned',
};

function AccountStatusBadge({ status, forceAdded }) {
  if (forceAdded) {
    return <span className="rh-badge rh-badge-force_added" style={{ fontSize: '0.72rem' }}>Force Added</span>;
  }
  if (!status) return <span style={{ color: 'var(--rh-text-muted)' }}>—</span>;
  return (
    <span className={`rh-badge rh-badge-${status}`} style={{ fontSize: '0.72rem' }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function AddedByBadges({ row }) {
  if (row?.is_unknown) {
    return <span style={{ color: 'var(--rh-text-muted)', fontSize: 12 }}>—</span>;
  }
  const badges = [];
  if (row?.added_by === 'self') {
    badges.push(
      <span
        key="self"
        className="rh-badge rh-badge-self-added"
        style={{ fontSize: '0.68rem' }}
        title="Added by the redditor from their dashboard"
      >
        Self-added
      </span>,
    );
    if (row.needs_self_add_review) {
      badges.push(
        <span
          key="new"
          className="rh-badge rh-badge-self-added-new"
          style={{ fontSize: '0.68rem' }}
          title="Needs niche / client-fit review"
        >
          New
        </span>,
      );
    }
  } else if (row?.added_by === 'invite') {
    badges.push(
      <span key="invite" className="rh-badge rh-badge-admin-added" style={{ fontSize: '0.68rem' }} title="Added via invite">
        Invite
      </span>,
    );
  } else if (row?.added_by === 'admin') {
    badges.push(
      <span key="admin" className="rh-badge rh-badge-admin-added" style={{ fontSize: '0.68rem' }} title="Added by an admin">
        Admin-added
      </span>,
    );
  }
  if (!badges.length) {
    return <span style={{ color: 'var(--rh-text-muted)', fontSize: 12 }}>—</span>;
  }
  return <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>{badges}</span>;
}

/* ─── Main component ─────────────────────────────────────────────────────── */

const PAGE_SIZE = 30;

export default function AccountPerformanceTab({ onSelfAddReviewCountChange } = {}) {
  const [accounts, setAccounts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  // Filters
  const [redditors, setRedditors]       = useState([]);
  const [redditorId, setRedditorId]     = useState('');
  const [clients, setClients]           = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [addedByFilter, setAddedByFilter] = useState(''); // '', self, admin, invite, needs_review

  // Sorting — default: deletion_rate descending
  const [sort, setSort] = useState({ col: 'deletion_rate', dir: 'desc' });

  // Show archived accounts toggle (off by default — archived accounts are hidden)
  const [showArchived, setShowArchived] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);

  // Expanded row (client breakdown) + collapsing animation tracking
  const [expandedId, setExpandedId]   = useState(null);
  const [collapsingId, setCollapsingId] = useState(null);
  const collapseTimer = useRef(null);

  // Client Fit modal (opened from niches column)
  const [fitModalAccount, setFitModalAccount] = useState(null);

  function toggleExpand(id) {
    if (expandedId === id) {
      // Start collapse: animate out, then unmount after transition
      setCollapsingId(id);
      setExpandedId(null);
      clearTimeout(collapseTimer.current);
      collapseTimer.current = setTimeout(() => setCollapsingId(null), 260);
    } else {
      // Immediately clear any in-progress collapse and open new row
      clearTimeout(collapseTimer.current);
      setCollapsingId(null);
      setExpandedId(id);
    }
  }

  async function markSelfAddReviewed(accountId) {
    if (!accountId || String(accountId).startsWith('unknown-')) return;
    try {
      await redditApi.post(`/admin/accounts/${accountId}/self-added-review/`);
      setAccounts((prev) => prev.map((a) => (
        String(a.id) === String(accountId)
          ? { ...a, needs_self_add_review: false, self_added_reviewed_at: new Date().toISOString() }
          : a
      )));
      onSelfAddReviewCountChange?.();
    } catch {
      // Non-blocking — niche modal still opens.
    }
  }

  function openFitModal(row) {
    if (row.is_unknown) return;
    setFitModalAccount({
      id: row.id,
      username: row.username,
      niches: row.niches || [],
      niche_fit: row.niche_fit || {},
    });
    if (row.needs_self_add_review) {
      markSelfAddReviewed(row.id);
    }
  }

  /* ── Fetch redditor list for filter dropdown ── */
  useEffect(() => {
    redditApi.get('/admin/redditors/')
      .then(({ data }) => setRedditors(Array.isArray(data) ? data : (data.results || [])))
      .catch(() => {});
  }, []);

  /* ── Fetch client list for filter dropdown ── */
  useEffect(() => {
    redditApi.get('/admin/clients/')
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.clients || []);
        setClients(list.map((c) => (typeof c === 'string' ? c : c.client_name)).filter(Boolean).sort());
      })
      .catch(() => {});
  }, []);

  /* ── Fetch account performance data ── */
  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (redditorId)   params.set('redditor_id', redditorId);
    if (clientFilter) params.set('client', clientFilter);
    if (dateFrom)     params.set('date_from', dateFrom);
    if (dateTo)       params.set('date_to', dateTo);
    if (showArchived) params.set('include_archived', 'true');
    const qs = params.toString();
    redditApi.get(`/admin/account-performance/${qs ? `?${qs}` : ''}`)
      .then(({ data }) => {
        setAccounts(data.accounts || []);
        if (typeof data.unreviewed_self_added === 'number') {
          onSelfAddReviewCountChange?.(data.unreviewed_self_added);
        }
      })
      .catch(() => setError('Failed to load account performance data.'))
      .finally(() => setLoading(false));
  }, [redditorId, clientFilter, dateFrom, dateTo, showArchived, onSelfAddReviewCountChange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 when filters or sort change
  useEffect(() => { setPage(1); setExpandedId(null); setCollapsingId(null); }, [redditorId, clientFilter, dateFrom, dateTo, addedByFilter, sort]);

  /* ── Sorting ── */
  function handleSort(col) {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'desc' }
    );
  }

  const filtered = accounts.filter((a) => {
    if (!addedByFilter) return true;
    if (addedByFilter === 'needs_review') return !!a.needs_self_add_review;
    return a.added_by === addedByFilter;
  });

  const sorted = [...filtered].sort((a, b) => {
    // Keep unreviewed self-added near the top when sorting by deletion rate default.
    if (sort.col === 'deletion_rate' && a.needs_self_add_review !== b.needs_self_add_review) {
      return a.needs_self_add_review ? -1 : 1;
    }
    let av, bv;
    if (sort.col === 'validation_status') {
      av = a.is_archived ? 'archived' : (a.force_added ? 'force_added' : a.validation_status);
      bv = b.is_archived ? 'archived' : (b.force_added ? 'force_added' : b.validation_status);
    } else {
      av = a[sort.col]; bv = b[sort.col];
    }
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av == null) av = '';
    if (bv == null) bv = '';
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  /* ── Redditor options ── */
  const redditorOptions = [
    { value: '', label: 'All Redditors' },
    ...redditors
      .filter((r) => !r.is_admin && !r.is_accountant)
      .map((r) => ({ value: r.id, label: r.full_name })),
  ];

  /* ── Summary KPIs ── */
  // "Unknown Account" rows are virtual buckets (comments with no linked account),
  // so they are excluded from the real account count but still contribute their
  // submitted/deleted totals so the KPIs reconcile with the Payments tab.
  const totalAccounts    = filtered.filter((a) => !a.is_unknown).length;
  const totalSubmitted   = filtered.reduce((s, a) => s + a.submitted, 0);
  const totalDeleted     = filtered.reduce((s, a) => s + a.deleted + a.instant_deleted, 0);
  const overallRate      = totalSubmitted > 0
    ? Math.round(totalDeleted / totalSubmitted * 1000) / 10
    : 0;
  const needsReviewCount = accounts.filter((a) => a.needs_self_add_review).length;

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const sortProps = { sort, onSort: handleSort };

  /* ── CSV export ── */
  function exportCSV() {
    const headers = [
      'Redditor', 'Account', 'Added by', 'Status', 'Niches', 'Clients', 'Subreddits',
      'Total', 'Submitted', 'Live', 'Deleted', 'Instant Deleted',
      'Deletion Rate (%)', 'Instant Del Rate (%)', 'Last Used',
    ];

    const statusText = (row) => {
      if (row.is_archived) return 'Archived';
      if (row.force_added) return 'Force Added';
      return STATUS_LABELS[row.validation_status] || row.validation_status || '';
    };

    const addedByText = (row) => {
      if (row.is_unknown) return '';
      if (row.added_by === 'self') {
        return row.needs_self_add_review ? 'Self-added (New)' : 'Self-added';
      }
      if (row.added_by === 'invite') return 'Invite';
      if (row.added_by === 'admin') return 'Admin-added';
      return row.added_by || '';
    };

    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const rows = sorted.flatMap((row) => {
      const acctLabel = row.is_unknown ? 'Unknown Account' : `u/${row.username}`;
      // Mirror the expanded-row logic: multi-subreddit clients → one row per subreddit
      const childRows = (row.clients_detail || []).flatMap((cd) => {
        const srRows = cd.subreddits_detail || [];
        if (srRows.length > 1) {
          return srRows.map((sr) => [
            row.redditor_name,
            acctLabel,
            addedByText(row),
            statusText(row),
            (row.niches || []).join('; '),
            cd.client_name,
            sr.subreddit,
            sr.submitted,
            sr.submitted,
            sr.live,
            sr.deleted,
            sr.instant_deleted,
            sr.deletion_rate,
            sr.instant_deletion_rate,
            sr.last_comment_date || '',
          ].map(escape).join(','));
        }
        return [[
          row.redditor_name,
          acctLabel,
          addedByText(row),
          statusText(row),
          (row.niches || []).join('; '),
          cd.client_name,
          (cd.subreddits || []).join('; '),
          cd.submitted,
          cd.submitted,
          cd.live,
          cd.deleted,
          cd.instant_deleted,
          cd.deletion_rate,
          cd.instant_deletion_rate,
          cd.last_comment_date || '',
        ].map(escape).join(',')];
      });

      // If account has only one client+subreddit, just export the top-level row
      if (childRows.length <= 1) {
        return [[
          row.redditor_name,
          acctLabel,
          addedByText(row),
          statusText(row),
          (row.niches || []).join('; '),
          (row.clients || []).join('; '),
          (row.subreddits || []).join('; '),
          row.total,
          row.submitted,
          row.live,
          row.deleted,
          row.instant_deleted,
          row.deletion_rate,
          row.instant_deletion_rate,
          row.last_comment_date || '',
        ].map(escape).join(',')];
      }

      return childRows;
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const now = new Date();
    const dd   = String(now.getDate()).padStart(2, '0');
    const mm   = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    a.download = `RedditHub_Account_Performance_${dd}_${mm}_${yyyy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="rh-tab-inner">

      {/* Header */}
      <div className="rh-section-header" style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 className="rh-section-title" style={{ margin: 0 }}>Account Performance</h2>
          <p className="rh-section-subtitle" style={{ margin: '4px 0 0', color: 'var(--rh-text-muted)', fontSize: '0.85rem' }}>
            Deletion rates across all Reddit accounts and Redditors
            {needsReviewCount > 0 && (
              <> · <strong style={{ color: 'var(--rh-orange)' }}>{needsReviewCount} self-added</strong> awaiting niche review</>
            )}
          </p>
        </div>
        {!loading && !error && sorted.length > 0 && (
          <button
            type="button"
            onClick={exportCSV}
            className="rh-btn rh-btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        )}
      </div>

      {/* KPI summary row */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {[
          { label: 'Accounts', value: totalAccounts },
          { label: 'Total Submitted', value: totalSubmitted },
          { label: 'Total Deleted', value: totalDeleted },
          { label: 'Overall Deletion Rate', value: `${overallRate}%` },
        ].map(({ label, value }) => (
          <div key={label} className="rh-card"
            style={{ flex: '1 1 140px', padding: '12px 16px', minWidth: '120px' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
              {label}
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rh-card" style={{ padding: '12px 16px', marginBottom: '16px' }}>
        <div className="rh-filter-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Redditor
            </label>
            <Select
              value={redditorId}
              onChange={setRedditorId}
              options={redditorOptions}
              placeholder="All Redditors"
            />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Client
            </label>
            <Select
              value={clientFilter}
              onChange={setClientFilter}
              options={[
                { value: '', label: 'All Clients' },
                ...clients.map((c) => ({ value: c, label: c })),
              ]}
              placeholder="All Clients"
            />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              Added by
            </label>
            <Select
              value={addedByFilter}
              onChange={setAddedByFilter}
              options={[
                { value: '', label: 'All sources' },
                { value: 'needs_review', label: `Needs review${needsReviewCount ? ` (${needsReviewCount})` : ''}` },
                { value: 'self', label: 'Self-added' },
                { value: 'admin', label: 'Admin-added' },
                { value: 'invite', label: 'Invite' },
              ]}
              placeholder="All sources"
            />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              From date
            </label>
            <RhDatePicker value={dateFrom} onChange={setDateFrom} placeholder="From date" />
          </div>

          <div>
            <label className="rh-filter-label" style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
              To date
            </label>
            <RhDatePicker value={dateTo} onChange={setDateTo} placeholder="To date" />
          </div>

          <div style={{ alignSelf: 'flex-end' }}>
            <ArchiveToggle
              checked={showArchived}
              onChange={setShowArchived}
              onLabel="Hide archived"
              offLabel="Show archived"
              title="Show archived (soft-hidden) accounts alongside active ones."
            />
          </div>

          {(redditorId || clientFilter || dateFrom || dateTo) && (
            <button
              type="button"
              className="rh-btn-secondary"
              style={{ alignSelf: 'flex-end' }}
              onClick={() => { setRedditorId(''); setClientFilter(''); setDateFrom(''); setDateTo(''); }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rh-card" style={{ padding: 0, overflow: 'hidden' }}>
        {error && (
          <div style={{ padding: '16px', color: 'var(--rh-danger)' }}>{error}</div>
        )}

        {!error && (
          <div className="rh-table-wrap">
            <table className="rh-table" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '120px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '90px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '190px' }} />
                <col style={{ width: '190px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '42px' }} />
                <col style={{ width: '52px' }} />
                <col style={{ width: '52px' }} />
                <col style={{ width: '42px' }} />
              </colgroup>
              <thead>
                <tr>
                  <SortTh col="redditor_name"         label="Redditor"  {...sortProps} />
                  <SortTh col="username"               label="Account"   {...sortProps} />
                  <SortTh col="added_by"               label="Added by"  title="Who onboarded this account: Self-added, Admin-added, or Invite" {...sortProps} />
                  <SortTh col="validation_status"      label="Status"    title="Account quality-check status (Passed / Failed / Force Added). Archived accounts show as Archived only." {...sortProps} />
                  <th style={{ whiteSpace: 'nowrap' }} title="AI-identified niches — click to view client fit">Niches</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Clients</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Subreddits</th>
                  <VerticalSortTh col="total"           label="Total"        title="All comments ever assigned to this account (including scheduled)" {...sortProps} />
                  <VerticalSortTh col="submitted"       label="Submitted"    title="Comments posted (submitted + live + deleted + instant deleted)" {...sortProps} />
                  <VerticalSortTh col="live"            label="Live"         {...sortProps} />
                  <VerticalSortTh col="deleted"         label="Deleted"      {...sortProps} />
                  <VerticalSortTh col="instant_deleted" label="Instant Del." title="Deleted While Submitting (DWS)" {...sortProps} />
                  <VerticalSortTh col="deletion_rate"          label="Deletion Rate"   title="Deleted ÷ Submitted × 100" {...sortProps} />
                  <VerticalSortTh col="instant_deletion_rate"  label="Instant Del Rate" title="Instant Deleted ÷ Submitted × 100" {...sortProps} />
                  <VerticalSortTh col="last_comment_date" label="Last Used"  title="Most recent published comment date" {...sortProps} />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={15} className="rh-empty">Loading…</td>
                  </tr>
                )}
                {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan={15} className="rh-empty">
                      {(redditorId || clientFilter || dateFrom || dateTo || addedByFilter)
                        ? 'No accounts have activity matching the current filters.'
                        : 'No accounts found.'}
                    </td>
                  </tr>
                )}
                {!loading && paginated.map((row) => {
                  const isExpanded   = expandedId === row.id;
                  const isCollapsing = collapsingId === row.id;
                  const showChildren = isExpanded || isCollapsing;
                  const isDimmed     = expandedId !== null && !isExpanded;
                  const canExpand    = row.clients && row.clients.length > 1;

                  const dimStyle = isDimmed
                    ? { opacity: 0.35, filter: 'blur(1.5px)', pointerEvents: 'none', transition: 'opacity 0.25s, filter 0.25s' }
                    : { transition: 'opacity 0.25s, filter 0.25s' };

                  const mainRowStyle = {
                    ...dimStyle,
                    cursor: canExpand ? 'pointer' : 'default',
                    ...(row.is_unknown && !isExpanded ? {
                      background: 'rgba(120, 120, 120, 0.05)',
                    } : {}),
                    ...(isExpanded ? {
                      background: 'var(--rh-bg-alt, #f8f9fb)',
                      boxShadow: 'inset 2px 0 0 var(--rh-orange, #e55)',
                    } : {}),
                  };

                  const childStyle = (i) => ({
                    background: 'rgba(255, 100, 0, 0.03)',
                    boxShadow: 'inset 2px 0 0 var(--rh-orange, #e55)',
                    opacity:    isCollapsing ? 0 : 1,
                    transform:  isCollapsing ? 'translateY(-4px)' : 'translateY(0)',
                    transition: isCollapsing
                      ? `opacity 0.22s ease ${i * 30}ms, transform 0.22s ease ${i * 30}ms`
                      : `opacity 0.22s ease ${i * 40 + 40}ms, transform 0.22s ease ${i * 40 + 40}ms`,
                  });

                  return (
                    <React.Fragment key={row.id}>
                      {/* ── Main account row — click anywhere to expand ── */}
                      <tr style={mainRowStyle} onClick={() => canExpand && toggleExpand(row.id)}>
                        <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.redditor_name}</td>
                        <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.is_unknown ? (
                            <span
                              className="rh-badge rh-badge-archived"
                              title="Comments not linked to any Reddit account (e.g. the account was removed). Still counted and payable."
                              style={{ fontFamily: 'inherit', fontSize: '0.72rem', fontStyle: 'italic', borderStyle: 'dashed' }}
                            >
                              Unknown Account
                            </span>
                          ) : (
                            `u/${row.username}`
                          )}
                        </td>
                        <td style={{ whiteSpace: 'normal', lineHeight: 1.8 }}>
                          <AddedByBadges row={row} />
                        </td>
                        <td style={{ whiteSpace: 'normal', lineHeight: 1.8 }}>
                          {row.is_banned ? (
                            <span
                              className="rh-badge rh-badge-banned"
                              title={
                                row.banned_at
                                  ? `Banned ${new Date(row.banned_at).toLocaleString()}`
                                  : 'Banned on Reddit'
                              }
                              style={{ fontSize: '0.7rem' }}
                            >
                              Banned
                            </span>
                          ) : row.is_archived ? (
                            <span
                              className="rh-badge rh-badge-archived"
                              title={
                                row.redditor_is_active === false
                                  ? 'Archived — Redditor deactivated'
                                  : row.archived_at
                                    ? `Archived ${new Date(row.archived_at).toLocaleString()}`
                                    : 'Archived'
                              }
                              style={{ fontSize: '0.7rem' }}
                            >
                              Archived
                            </span>
                          ) : (
                            <AccountStatusBadge status={row.validation_status} forceAdded={row.force_added} />
                          )}
                        </td>

                        {/* Niches — click opens Client Fit modal (stopPropagation so row expand still works) */}
                        <td
                          onClick={(e) => {
                            e.stopPropagation();
                            openFitModal(row);
                          }}
                          style={{
                            cursor: row.is_unknown ? 'default' : 'pointer',
                            maxWidth: 180,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                          title={row.is_unknown ? undefined : 'View client fit'}
                        >
                          {row.is_unknown ? (
                            <span style={{ color: 'var(--rh-text-muted)' }}>—</span>
                          ) : (
                            <NicheTags
                              niches={row.niches}
                              max={1}
                              preferShortest
                              nowrap
                              emptyText="—"
                            />
                          )}
                        </td>

                        {/* Clients cell — chevron indicator (row itself is clickable) */}
                        <td style={{ whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                            <PillList items={row.clients} colorClass="rh-badge-submitted" />
                            {canExpand && (
                              <span style={{
                                flexShrink: 0,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '20px', height: '20px', borderRadius: '50%',
                                background: isExpanded ? 'var(--rh-orange, #e55)' : 'var(--rh-bg-alt, #ebebeb)',
                                color: isExpanded ? '#fff' : 'var(--rh-text-muted)',
                                transition: 'background 0.22s, color 0.22s, transform 0.22s',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              }}>
                                <svg width="9" height="9" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </span>
                            )}
                          </div>
                        </td>

                        <td style={{ whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden' }}>
                          <PillList items={row.subreddits} colorClass="rh-badge-archived" />
                        </td>
                        <td style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--rh-text-muted)' }}>{row.total}</td>
                        <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>{row.submitted}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="rh-badge rh-badge-live" style={{ minWidth: '28px', textAlign: 'center' }}>{row.live}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {row.deleted > 0
                            ? <span className="rh-badge rh-badge-deleted" style={{ minWidth: '28px', textAlign: 'center' }}>{row.deleted}</span>
                            : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {row.instant_deleted > 0
                            ? <span className="rh-badge rh-badge-dws" style={{ minWidth: '28px', textAlign: 'center' }}>{row.instant_deleted}</span>
                            : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'center', padding: '6px 4px' }}><RateCell rate={row.deletion_rate} compact /></td>
                        <td style={{ textAlign: 'center', padding: '6px 4px' }}><RateCell rate={row.instant_deletion_rate} compact /></td>
                        <td style={{ textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          {row.last_comment_date || '—'}
                        </td>
                      </tr>

                      {/* ── Per-client/subreddit child rows ── */}
                      {showChildren && (row.clients_detail || []).flatMap((cd, ci) => {
                        const srRows = (cd.subreddits_detail || []);
                        // If client has multiple subreddits, skip the client summary and show individual sr rows
                        if (srRows.length > 1) {
                          return srRows.map((sr, si) => (
                            <tr
                              key={`${row.id}-${cd.client_name}-${sr.subreddit}`}
                              style={childStyle(ci + si)}
                            >
                              <td style={{ paddingLeft: '24px', color: 'var(--rh-text-muted)', fontSize: '0.78rem' }}>↳</td>
                              <td />
                              <td />
                              <td />
                              <td />
                              <td style={{ maxWidth: 180, overflow: 'hidden' }}>
                                <span className="rh-badge rh-badge-submitted" style={{ fontSize: '0.75rem', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {cd.client_name}
                                </span>
                              </td>
                              <td style={{ maxWidth: 180, overflow: 'hidden' }}>
                                <span className="rh-badge rh-badge-archived" style={{ fontSize: '0.75rem', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {sr.subreddit}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center', color: 'var(--rh-text-muted)' }}>—</td>
                              <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>{sr.submitted}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span className="rh-badge rh-badge-live" style={{ minWidth: '28px', textAlign: 'center' }}>{sr.live}</span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {sr.deleted > 0
                                  ? <span className="rh-badge rh-badge-deleted" style={{ minWidth: '28px', textAlign: 'center' }}>{sr.deleted}</span>
                                  : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {sr.instant_deleted > 0
                                  ? <span className="rh-badge rh-badge-dws" style={{ minWidth: '28px', textAlign: 'center' }}>{sr.instant_deleted}</span>
                                  : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                              </td>
                              <td style={{ textAlign: 'center', padding: '6px 4px' }}><RateCell rate={sr.deletion_rate} compact /></td>
                              <td style={{ textAlign: 'center', padding: '6px 4px' }}><RateCell rate={sr.instant_deletion_rate} compact /></td>
                              <td style={{ textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                {sr.last_comment_date || '—'}
                              </td>
                            </tr>
                          ));
                        }

                        // Single subreddit — show the client row as before
                        return [(
                          <tr key={`${row.id}-${cd.client_name}`} style={childStyle(ci)}>
                            <td style={{ paddingLeft: '24px', color: 'var(--rh-text-muted)', fontSize: '0.78rem' }}>↳</td>
                            <td />
                            <td />
                            <td />
                            <td />
                            <td style={{ maxWidth: 180, overflow: 'hidden' }}>
                              <span className="rh-badge rh-badge-submitted" style={{ fontSize: '0.75rem', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {cd.client_name}
                              </span>
                            </td>
                            <td style={{ maxWidth: 180, overflow: 'hidden' }}><PillList items={cd.subreddits} colorClass="rh-badge-archived" /></td>
                            <td style={{ textAlign: 'center', color: 'var(--rh-text-muted)' }}>—</td>
                            <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>{cd.submitted}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className="rh-badge rh-badge-live" style={{ minWidth: '28px', textAlign: 'center' }}>{cd.live}</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {cd.deleted > 0
                                ? <span className="rh-badge rh-badge-deleted" style={{ minWidth: '28px', textAlign: 'center' }}>{cd.deleted}</span>
                                : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {cd.instant_deleted > 0
                                ? <span className="rh-badge rh-badge-dws" style={{ minWidth: '28px', textAlign: 'center' }}>{cd.instant_deleted}</span>
                                : <span style={{ color: 'var(--rh-text-muted)' }}>—</span>}
                            </td>
                            <td style={{ textAlign: 'center', padding: '6px 4px' }}><RateCell rate={cd.deletion_rate} compact /></td>
                            <td style={{ textAlign: 'center', padding: '6px 4px' }}><RateCell rate={cd.instant_deletion_rate} compact /></td>
                            <td style={{ textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {cd.last_comment_date || '—'}
                            </td>
                          </tr>
                        )];
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && sorted.length > 0 && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          {/* Pagination controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              type="button"
              className="rh-btn rh-btn-secondary"
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              disabled={page === 1}
              onClick={() => { setPage(1); setExpandedId(null); }}
            >
              «
            </button>
            <button
              type="button"
              className="rh-btn rh-btn-secondary"
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              disabled={page === 1}
              onClick={() => { setPage((p) => Math.max(1, p - 1)); setExpandedId(null); }}
            >
              ‹
            </button>
            <span style={{ fontSize: '0.8rem', color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>
              Page {page} of {totalPages} &nbsp;·&nbsp; {sorted.length} account{sorted.length !== 1 ? 's' : ''}
            </span>
            <button
              type="button"
              className="rh-btn rh-btn-secondary"
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              disabled={page === totalPages}
              onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); setExpandedId(null); }}
            >
              ›
            </button>
            <button
              type="button"
              className="rh-btn rh-btn-secondary"
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
              disabled={page === totalPages}
              onClick={() => { setPage(totalPages); setExpandedId(null); }}
            >
              »
            </button>
          </div>

          {/* Footer note */}
          <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--rh-text-muted)' }}>
            {PAGE_SIZE} per page.
            Deletion Rate = Deleted ÷ Submitted × 100.
            Instant Del Rate = Instant Deleted ÷ Submitted × 100.
            Hover column headers for details.
          </p>
        </div>
      )}

      {fitModalAccount && (
        <NicheFitModal
          account={fitModalAccount}
          onClose={() => setFitModalAccount(null)}
        />
      )}

    </div>
  );
}
